import invariant from 'invariant';
import { DISPLAY_TEXT_FOR_SURVEY_STATE } from '../companySurveys/utils';
import DateTimeUtils from '../utils/DateTimeUtils';
import { EMPTY_VALUE_DASH } from '../utils/SuppliersUtils';
import { displayableAttributeValue } from '../utils/analytics';
import assertNever from '../utils/assertNever';
import {
  SBT_COMMITMENT_STAGE_LABELS,
  getClimateProgressLabel,
} from '../utils/companyUtils';
import { formatNumber, formatPercentageSmart } from '../utils/helpers';
import isNotNullish from '../utils/isNotNullish';
import isNullish from '../utils/isNullish';
import { SupplierColumnName } from './SupplierColumnRegistry';
import { Supplier, SupplierTableRow } from './supplierTypes';
import { kgToTonnes } from '../fund/math';

export type ColumnValueFormatter<T extends Supplier> = (supplier: T) => string;

export const formatSupplierName: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return supplier.name;
};

export const formatPriority: ColumnValueFormatter<Supplier> = (supplier) => {
  return supplier.priority ?? 'None';
};

export const formatEmissionsRank: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return isNotNullish(supplier.emissionsRank)
    ? formatNumber(supplier.emissionsRank, 0)
    : '-';
};

export const formatTotalEmissions: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return isNotNullish(supplier.totalKgco2e)
    ? formatNumber(kgToTonnes(supplier.totalKgco2e), 2)
    : '-';
};

export const formatSpend: ColumnValueFormatter<Supplier> = (supplier) => {
  return isNotNullish(supplier.totalSpendUsd)
    ? formatNumber(supplier.totalSpendUsd)
    : '-';
};

export const formatPercentEmissions: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return formatPercentageSmart(supplier.percentEmissions, {
    fixedRoundingPlaces: 2,
  });
};

export const formatTopCategorizedEmission: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  if (
    isNullish(supplier.topCategorizedEmissionBusinessCategory) ||
    isNullish(supplier.topCategorizedEmissionKgco2e) ||
    isNullish(supplier.topCategorizedEmissionProportion)
  ) {
    return '';
  }
  return displayableAttributeValue(
    supplier.topCategorizedEmissionBusinessCategory ??
      supplier.topCategorizedEmissionBusinessSubcategory
  );
};

export const formatDisclosures: ColumnValueFormatter<Supplier> = (supplier) => {
  return getClimateProgressLabel(supplier.climateProgress);
};

export const formatNetZero: ColumnValueFormatter<Supplier> = (supplier) => {
  return isNotNullish(supplier.netZeroCommitmentTargetYear)
    ? `Net zero by ${supplier.netZeroCommitmentTargetYear}`
    : 'None';
};

export const formatCarbonNeutral: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return isNotNullish(supplier.carbonNeutralCommitmentTargetYear)
    ? `Carbon neutral by ${supplier.carbonNeutralCommitmentTargetYear}`
    : 'None';
};

export const formatCleanEnergy: ColumnValueFormatter<Supplier> = (supplier) => {
  const year = supplier.cleanEnergyCommitmentTargetYear;
  const percentage = supplier.cleanEnergyCommitmentTargetPercentage;

  if (year && percentage) {
    return `${percentage}% by ${year}`;
  } else if (percentage) {
    return `Committed to ${percentage}%`;
  } else if (year) {
    return `Committed by ${year}`;
  }

  if (supplier.companyId) {
    return 'None';
  } else {
    return 'Unknown';
  }
};

export const formatSbtCommitmentStage: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return SBT_COMMITMENT_STAGE_LABELS[supplier.sbtiStage];
};

export const formatNotes: ColumnValueFormatter<Supplier> = (supplier) => {
  return supplier.notes ?? '';
};

export const formatEngagementCohorts: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return supplier.engagementCohortIds.join(', ');
};

export const formatContacts: ColumnValueFormatter<Supplier | SupplierTableRow> =
  (supplier) => {
    // hack to check if object is a SupplierTableRow
    // if it is not, then we're not in the suppliers table and this formatter should not be used
    if ('contactIds' in supplier) {
      return supplier.contactIds
        .map((_, idx) => {
          const email = supplier.contactEmails[idx];
          return `${email}`;
        })
        .join(', ');
    }
    // Choose to return empty string instead of error, since this is just a formatting thing.
    return '';
  };

export const formatLatestDisclosureDate: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return isNotNullish(supplier.latestDisclosureDateTime)
    ? DateTimeUtils.formatDate(
        DateTimeUtils.fromJSDate(supplier.latestDisclosureDateTime)
      )
    : '';
};

export const formatLatestCdpDisclosurePublishingYear: ColumnValueFormatter<
  Supplier
> = (supplier) => {
  return isNotNullish(supplier.latestCdpDisclosurePublishingYear)
    ? supplier.latestCdpDisclosurePublishingYear.toString()
    : 'N/A';
};

export const formatSurveyState: ColumnValueFormatter<
  Supplier | SupplierTableRow
> = (supplier) => {
  // hack to check if object is a SupplierTableRow
  // if it is not, then we're not in the suppliers table and this formatter should not be used
  if ('mostActionableTaskStatus' in supplier) {
    return isNotNullish(supplier.mostActionableTaskStatus)
      ? DISPLAY_TEXT_FOR_SURVEY_STATE[supplier.mostActionableTaskStatus]
      : 'None';
  }
  // Choose to return empty string instead of error, since this is just a formatting thing.
  return '';
};

export const formatSurveyPortalUrl: ColumnValueFormatter<
  Supplier | SupplierTableRow
> = (supplier) => {
  invariant(
    // eslint-disable-next-line no-restricted-globals
    typeof window !== 'undefined',
    'window must be defined to format survey portal urls'
  );
  // hack to check if object is a SupplierTableRow
  // if it is not, then we're not in the suppliers table and this formatter should not be used
  if ('surveyPortalUrl' in supplier) {
    return isNotNullish(supplier.surveyPortalUrl)
      ? // eslint-disable-next-line no-restricted-globals
        `${window.location.origin}${supplier.surveyPortalUrl}`
      : '';
  }
  // Choose to return empty string instead of error, since this is just a formatting thing.
  return '';
};

export const formatPercentSpend: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  return isNotNullish(supplier.percentSpend)
    ? formatPercentageSmart(supplier.percentSpend, {
        fixedRoundingPlaces: 2,
      })
    : EMPTY_VALUE_DASH;
};

export const formatGhgCategoryIds: ColumnValueFormatter<Supplier> = (
  supplier
) => {
  const ghgCategoryIdsNumbers = new Set(
    supplier.ghgCategoryIds
      .map((category) => category.split(' ').shift())
      .filter(isNotNullish)
  );
  return Array.from(ghgCategoryIdsNumbers).sort().join(', ');
};

export const formatTargets: ColumnValueFormatter<Supplier> = (supplier) => {
  return supplier.targets ?? '';
};

export function getFormatterForField(field: SupplierColumnName) {
  return (supplier: Supplier | SupplierTableRow): string => {
    switch (field) {
      case 'supplierName':
        return formatSupplierName(supplier);
      case 'priority':
        return formatPriority(supplier);
      case 'emissionsRank':
        return formatEmissionsRank(supplier);
      case 'totalEmissions':
        return formatTotalEmissions(supplier);
      case 'spend':
        return formatSpend(supplier);
      case 'percentEmissions':
        return formatPercentEmissions(supplier);
      case 'topCategorizedEmission':
        return formatTopCategorizedEmission(supplier);
      case 'disclosures':
        return formatDisclosures(supplier);
      case 'netZero':
        return formatNetZero(supplier);
      case 'carbonNeutral':
        return formatCarbonNeutral(supplier);
      case 'cleanEnergy':
        return formatCleanEnergy(supplier);
      case 'sbtCommitmentStage':
        return formatSbtCommitmentStage(supplier);
      case 'notes':
        return formatNotes(supplier);
      case 'engagementCohorts':
        return formatEngagementCohorts(supplier);
      case 'contacts':
        return formatContacts(supplier);
      case 'latestDisclosureDate':
        return formatLatestDisclosureDate(supplier);
      case 'latestCdpDisclosurePublishingYear':
        return formatLatestCdpDisclosurePublishingYear(supplier);
      case 'surveyState':
        return formatSurveyState(supplier);
      case 'surveyPortalUrl':
        return formatSurveyPortalUrl(supplier);
      case 'ghgCategoryIds':
        return formatGhgCategoryIds(supplier);
      case 'targets':
        return formatTargets(supplier);
      case 'emissionsFactors':
        return '';
      case 'percentSpend':
        return formatPercentSpend(supplier);
      case 'supplierDetailsUrl':
        return '';
      default:
        assertNever(field);
    }
  };
}
